import { Fragment, useState } from 'react';
import { primeiroYoutube, segmentarTexto } from '@/conversas/links';

/**
 * O texto de uma mensagem, com as URLs clicáveis.
 *
 * Substitui o `<p>{mensagem.texto}</p>` que havia na bolha, e mantém o `<p>`
 * **de propósito**: o CSS que faz a bolha funcionar mora em `.msg-corpo p`
 * (`white-space: pre-wrap` e `overflow-wrap: anywhere`). Trocar por `<div>`
 * perderia as quebras de linha que o paciente escreveu e deixaria um endereço
 * comprido estourar a largura da bolha.
 *
 * A decisão de o que é link é do módulo puro `conversas/links.js` — aqui só se
 * desenha. E se desenha com elementos React, **nunca**
 * `dangerouslySetInnerHTML`: é o React escapando o texto que impede o que o
 * paciente digitou de virar marcação.
 */
export default function TextoComLinks({ texto, className }) {
  const segmentos = segmentarTexto(texto);

  return (
    <p className={className}>
      {segmentos.map((segmento, i) =>
        segmento.tipo === 'link' ? (
          <a
            key={i}
            href={segmento.href}
            target="_blank"
            // `noopener` é obrigatório, não enfeite: sem ele a página aberta
            // recebe `window.opener` e pode trocar a aba de origem por uma
            // cópia falsa do OdontoSDR (tabnabbing) — e o atendente digitaria
            // a senha nela. `noreferrer` cobre navegador antigo.
            rel="noopener noreferrer"
          >
            {segmento.valor}
          </a>
        ) : (
          // Fragment com key: um segmento de texto é string pura, e uma string
          // não carrega key por si.
          <Fragment key={i}>{segmento.valor}</Fragment>
        ),
      )}
    </p>
  );
}

/**
 * A capa do vídeo, como o WhatsApp faz — um cartão clicável abaixo do texto.
 *
 * **Nada é buscado na web.** A miniatura vem de uma URL pública e previsível do
 * próprio YouTube, montada a partir do id extraído do link. Sem chamada de API,
 * sem chave, sem servidor no meio: só um `<img src>`.
 *
 * Renderiza `null` quando não há vídeo no texto, então dá para chamar sem
 * condicional em volta.
 */
export function CapaYoutube({ texto }) {
  // Vídeo removido pelo dono devolve uma imagem cinza de "indisponível", mas
  // um id que já não existe pode dar 404 — e um ícone de imagem quebrada dentro
  // da bolha é pior que cartão nenhum. O hook vem antes do early return porque
  // a ordem dos hooks não pode variar entre renders.
  const [falhou, setFalhou] = useState(false);
  const video = primeiroYoutube(texto);

  if (!video || falhou) return null;

  return (
    <a
      className="msg-youtube"
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      title="Abrir no YouTube"
    >
      <img
        src={video.capa}
        alt="Capa do vídeo no YouTube"
        loading="lazy"
        onError={() => setFalhou(true)}
      />
      <span className="msg-youtube-play" aria-hidden="true">
        ▶
      </span>
    </a>
  );
}
